import {
  Avatar,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import heic2any from "heic2any";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { setIsBottomBarVisible } from "../redux/slices/commonSlice";
import { setNotification } from "../redux/slices/notificationsSlice";
import { getMe, updateUserById } from "../redux/slices/userSlice";
import RemoveIcon from "@mui/icons-material/Delete";
import Parse from "parse";

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profilePicRef = useRef();

  const [email, setEmail] = useState();
  const [username, setUsername] = useState();
  const [phone, setPhone] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const userMe = useSelector((state) => state.user.userMe);
  const updateUserLoading = useSelector(
    (state) => state.user.updateUserLoading
  );
  const currentUserId = useSelector((state) => state.user.currentUserId);
  const updateUserError = useSelector((state) => state.user.updateUserError);

  const updateUser = async () => {
    let imgFile = null;
    if (selectedFile) {
      const isFile = selectedFile?.__type === "File";
      imgFile = !isFile
        ? new Parse.File(selectedFile.name, selectedFile)
        : selectedFile;
      !isFile && (await imgFile.save());
    }

    const payload = {
      username,
      phone,
      userId: currentUserId,
      avatar: imgFile,
    };
    dispatch(updateUserById(payload)).then(
      (res) => {
        dispatch(getMe());
        dispatch(
          setNotification({
            show: true,
            message: t("notifications.successSavedData"),
            severity: "success",
          })
        );
      },
      (err) => {
        dispatch(
          setNotification({
            show: true,
            message: updateUserError.message,
            severity: "error",
          })
        );
      }
    );
  };
  const onCancelUpdateUser = () => {
    dispatch(setIsBottomBarVisible(true));
    navigate("/home/gift-page");
  };
  const onPhoneInputChange = (event) => {
    setPhone(event.target.value);
  };
  const handleUploadClick = async (event) => {
    let file = null;
    if (event.target.files[0] && event.target.files[0].name.includes(".HEIC")) {
      setImageUploading(true);
      setSelectedFile(null);
      // get image as blob url
      let blobURL = URL.createObjectURL(event.target.files[0]);

      // convert "fetch" the new blob url
      let blobRes = await fetch(blobURL);

      // convert response to blob
      let blob = await blobRes.blob();

      // convert to PNG - response is blob
      let conversionResult = await heic2any({ blob, quality: 0.5 });
      file = conversionResult;
    } else if (event.target?.files) {
      setImageUploading(true);
      setSelectedFile(null);
      file = event.target.files[0];
    }

    setImageUrl(URL.createObjectURL(file));
    setSelectedFile(file);
    profilePicRef.current.value = "";
  };
  const showImageBox = () => {
    return imageUrl && selectedFile;
  };
  const removeImage = () => {
    URL.revokeObjectURL(selectedFile);
    setSelectedFile(null);
    setImageUrl(null);
    profilePicRef.current.value = "";
  };

  useEffect(() => {
    if (userMe) {
      setEmail(userMe.email);
      setUsername(userMe.username);
      setPhone(userMe.phone);
      // setImageUrl(userMe.avatar.url);
    }
    dispatch(setIsBottomBarVisible(false));
  }, [userMe]);
  useEffect(() => {
    if (selectedFile) {
      setImageUploading(false);
    }
  }, [selectedFile]);

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        marginTop: "24px",
      }}
    >
      <Typography variant="h5" align="center">
        {t("profile.profilePage")}
      </Typography>
      <Grid container spacing={2} padding="12px" marginTop="16px">
        {userMe && !showImageBox() && (
          <Stack
            display="flex"
            direction="row"
            justifyContent="center"
            width="100%"
            paddingLeft="16px"
          >
            <Stack>
              <img src={userMe.avatar._url} width="110px" alt="profile" />
            </Stack>
          </Stack>
        )}
        <Grid item xs={12}>
          <Stack
            display="flex"
            direction="row"
            justifyContent="center"
            width="100%"
          >
            <input
              ref={profilePicRef}
              id="contained-button-file"
              type="file"
              multiple
              onChange={handleUploadClick}
              style={{ display: "none" }}
            />
            {!imageUrl && (
              <label htmlFor="contained-button-file">
                <Button
                  disabled={!!imageUploading || !!imageUrl}
                  variant="contained"
                  color="primary"
                  component="span"
                >
                  {t("profile.changeAvatar")}
                </Button>
              </label>
            )}
          </Stack>
          {showImageBox() && (
            <Box
              mt={2}
              textAlign="start"
              justify="start"
              style={{ position: "relative" }}
            >
              <Stack
                display="flex"
                direction="column"
                justifyContent="center"
                alignItems="center"
              >
                <Avatar
                  alt="profile"
                  src={imageUrl}
                  sx={{ width: 100, height: 100 }}
                />
                <Button
                  sx={{ marginTop: "12px" }}
                  onClick={removeImage}
                  variant="contained"
                  color="error"
                  startIcon={<RemoveIcon />}
                >
                  {t("qrCodeScanner.remove")}
                </Button>
              </Stack>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="email"
            label={t("profile.email")}
            type="text"
            value={email}
            fullWidth
            disabled
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="username"
            label={t("profile.username")}
            type="text"
            value={username}
            fullWidth
            disabled
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="phone"
            label={t("profile.phone")}
            type="text"
            value={phone}
            onChange={onPhoneInputChange}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Stack
          direction="row"
          justifyContent="center"
          width="100%"
          marginTop="12px"
        >
          <Button
            color="primary"
            type="submit"
            onClick={onCancelUpdateUser}
            sx={{ marginRight: "12px" }}
          >
            {t("profile.cancel")}
          </Button>
          <Button
            disabled={!phone}
            variant="contained"
            color="error"
            type="submit"
            onClick={updateUser}
          >
            {updateUserLoading === "PENDING" ? (
              <Box sx={{ display: "flex" }}>
                <CircularProgress color="inherit" />
              </Box>
            ) : (
              t("profile.save")
            )}
          </Button>
        </Stack>
        <Grid item xs={12}>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            width="100%"
            marginTop="12px"
          >
            <Typography component="div" textAlign="center">
              {t("profile.additionalQuestions")}
            </Typography>
            <Stack display="flex" justifyContent="start">
              <Button
                variant="text"
                color="primary"
                target="_top"
                rel="noopener noreferrer"
                href={`mailto:cris.operator.1@cchellenic.com`}
              >
                <Typography variant="button" style={{ fontSize: "0.69rem" }}>
                  cris.operator.1@cchellenic.com
                </Typography>
              </Button>
              <Button
                variant="text"
                color="primary"
                target="_top"
                rel="noopener noreferrer"
                href={`mailto:cris.operator.3@cchellenic.com`}
              >
                <Typography variant="button" style={{ fontSize: "0.69rem" }}>
                  cris.operator.3@cchellenic.com
                </Typography>
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
export default ProfilePage;
